'use client';

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { MenuSnapshot } from '@/features/totems/domain/model/menu-snapshot';
import { totemMenuSnapshotQueryOptions, totemsKeys } from '@/features/totems/presentation/queries/totems.queries';
import { supabase } from '@/shared/infrastructure/supabase/client';

export function useTotemMenu(totemId: string, menuId: string | null | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery(totemMenuSnapshotQueryOptions(totemId, menuId));

  useEffect(() => {
    if (!menuId || !totemId) return;

    const channel = supabase
      .channel(`menu-version:${menuId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'menus',
          filter: `id=eq.${menuId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: totemsKeys.menuSnapshot(totemId, menuId),
          });
        }
      )
      .subscribe();

    const stockChannel = supabase
      .channel(`menu-stock:${totemId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'product_stock',
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: totemsKeys.menuSnapshot(totemId, menuId),
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      supabase.removeChannel(stockChannel);
    };
  }, [menuId, totemId, queryClient]);

  return {
    menu: query.data as MenuSnapshot | null | undefined,
    isLoading: query.isLoading,
    error: query.error,
  };
}
